import { CurrencyPipe } from '@angular/common'
import { Component, WritableSignal, computed, signal } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { FieldTypeConfig } from '@ngx-formly/core'
import { FieldType } from '@ngx-formly/material'
import { ButtonComponent } from 'src/app/components/button/button.component'
import { PaymentProfilesComponent } from 'src/app/components/payment.profiles/payment.profiles.component'
import { ApplicationService } from 'src/app/services/application.service'
import { BillingService } from 'src/app/services/billing.service'
import { AuthNetResponseOpaqueData, Merchant, PaymentMethodCC, PaymentMethodProfile, UpsellPayload } from 'src/types/billing'
import { OrderService } from 'src/app/services/order.service'

@Component({
	selector: 'gwc-payment-module',
	templateUrl: './payment.module.component.html',
	styleUrls: ['./payment.module.component.scss'],
	imports: [
		ReactiveFormsModule,
		PaymentProfilesComponent,
		CurrencyPipe,
		MatCheckboxModule,
		ButtonComponent
	]
})

export class PaymentModuleComponent extends FieldType<FieldTypeConfig> {
	public step: WritableSignal<string> = signal<string>('summary')
	public processingPayment: WritableSignal<boolean> = signal<boolean>(false)
	public paymentError: WritableSignal<string> = signal<string>('')
	public products: WritableSignal<any> = signal<any>(null)
	public payload: WritableSignal<UpsellPayload | null> = signal<UpsellPayload | null>(null)
	public total = computed(() => this.payload()?.addons.reduce((acc, addon) => acc + addon.expected_price, 0) ?? 0)
	public paymentMethod: WritableSignal<PaymentMethodCC | PaymentMethodProfile | null> = signal<PaymentMethodCC | PaymentMethodProfile | null>(null)
	public ccForm!: FormGroup
	public termsAccepted: FormControl<boolean> = new FormControl<boolean>(false, {
		nonNullable: true,
		validators: [(control: AbstractControl): ValidationErrors | null => control.value === true ? null : { required: true }]
	})

	constructor(
		private applicationService: ApplicationService,
		private orderService: OrderService,
		private billingService: BillingService
	) {
		super()
		this.ccForm = this.billingService.getCreditCardForm()
	}

	ngOnInit() {
		if (this.model.application.product.type === 'passport') {
			this.applicationService.getPassportProduct(this.model.application.product.subtype, this.orderService.active_domain, true)
				.subscribe((response: any) => {
					const addons = response[0].addons
					const digital_photo = addons.find((addon: any) => addon.subtype === 'digital_photo')

					this.payload.set({
						application_uuid: this.model.application.application_uuid,
						addons: [
							{
								name: digital_photo.label,
								service: 'Printing Service',
								action: 'add',
								uuid: digital_photo.uuid,
								expected_price: digital_photo.price/100
							}
						]
					})

					this.products.set({
						digital_photo: digital_photo
					})
				})
		}
	}

	public onProfileSelected(profile_uuid: string): void {
		if (profile_uuid) {
			this.paymentMethod.set({
				type: 'user_profile',
				user_payment_profile_uuid: profile_uuid
			})
		} else {
			this.paymentMethod.set({
				type: 'credit_card',
				merchant: '',
				payment_token: '',
				payment_descriptor: ''
			})
		}
	}

	public placeOrder(): void {
		this.processingPayment.set(true)
		this.paymentError.set('')

		if (this.termsAccepted.value && this.payload() && this.paymentMethod()) {
			if (this.paymentMethod()?.type === 'user_profile') {
				this.upgradeOrder()
			} else {
				if (this.ccForm.valid) {
					this.billingService.getMerchantToken(this.orderService.active_domain)
						.subscribe({
							next: (merchant: Merchant) => {
								this.getNonce(merchant)
							}
						})
				} else {
					this.ccForm.markAllAsTouched()
					this.processingPayment.set(false)
				}
			}
		} else {
			this.termsAccepted.markAsTouched()
			this.processingPayment.set(false)
		}
	}

	private async getNonce(merchant: Merchant) {
		try {
			const response: AuthNetResponseOpaqueData = await this.billingService.getNonce(this.ccForm.getRawValue(), merchant)
			this.paymentMethod.set({
				type: 'credit_card',
				merchant: merchant.merchant,
				payment_token: response.dataValue,
				payment_descriptor: response.dataDescriptor
			})

			this.upgradeOrder()
		} catch (e) {
			this.processingPayment.set(false)
		}
	}

	private upgradeOrder(): void {
		this.billingService.upgrade(this.payload()!, this.paymentMethod()!, this.orderService.active_order)
			.subscribe({
				next: response => {
					this.billingService.paymentCompleted.set(true)
					this.step.set('confirmation')
					this.processingPayment.set(false)
					this.model.application.addons.push('digital_photo')
				},
				error: error => {
					this.paymentError.set(error.error.message)
					this.processingPayment.set(false)
				}
			})
	}
}
